import React, { useMemo } from "react";

import { type ContractModel, type EntityModel } from "@doitintl/cmp-models";
import { Stack, Typography } from "@mui/material";

import DataCouplet from "../../../Components/DataCouplet";
import { type Entity } from "../../../Context/customer/EntitiesContext";
import { useCustomerContext } from "../../../Context/CustomerContext";
import { useTier } from "../../../Context/TierProvider";
import { getCurrencySymbol } from "../../../utils/common";
import { NotAvailable } from "../../Assets/Tabs/components/typographics";
import { paymentTermOptions } from "../ContractForm/const";
import { contractDate } from "../utils";
import GeneralSection from "./components/GeneralSection";

type Props = {
  contract: ContractModel;
  accountManagerName?: string;
  isSidePanel?: boolean;
};

const ContractPageDci = ({ contract, accountManagerName, isSidePanel }: Props) => {
  const { entities, entitiesLoading } = useCustomerContext();
  const { allTiers, loading: tiersLoading } = useTier();

  const entity = useMemo<Entity | undefined>(
    () => entities.find((ent) => ent.id === contract.entity?.id),
    [contract.entity?.id, entities]
  );

  const tier = useMemo(() => allTiers?.find((t) => t.ref.id === contract.tier?.id), [contract.tier, allTiers]);

  return (
    <Stack spacing={3}>
      {!isSidePanel && <Typography variant="h1">DoiT Cloud Intelligence™</Typography>}
      <GeneralSection
        entity={entity as EntityModel}
        entitiesLoading={entitiesLoading}
        accountManagerName={accountManagerName || NotAvailable}
        contract={contract}
        showPointOfSale={true}
      />
      <Stack spacing={1}>
        <Typography variant="h3">Contract details</Typography>
        <DataCouplet
          field="Subscription start date"
          value={contractDate(contract.startDate)}
          key="contract-start-date"
          divider={true}
        />
        <DataCouplet
          field="Subscription end date"
          value={contractDate(contract.endDate)}
          key="contract-end-date"
          divider={true}
        />
        <DataCouplet
          field="Tier"
          value={tier?.displayName ?? NotAvailable}
          key="tier"
          loading={tiersLoading}
          divider={true}
        />
        <DataCouplet
          field="Subscription term"
          value={contract.commitmentMonths ? `${contract.commitmentMonths} months` : NotAvailable}
          key="subscription-term"
          divider={true}
        />
        <DataCouplet
          field="Subscription type"
          value={contract.isCommitment ? "Commitment" : "On-demand"}
          divider={true}
        />
        <DataCouplet
          field="Payment term"
          value={paymentTermOptions.find((x) => x.value === contract.paymentTerm)?.label ?? NotAvailable}
          divider={true}
        />
        <DataCouplet
          field="Minimum service fee"
          value={
            contract.minimumServiceFee
              ? `${contract.currency && getCurrencySymbol(contract.currency)}${contract.minimumServiceFee}`
              : NotAvailable
          }
          divider={true}
        />
        <DataCouplet
          field="Monthly cloud spend percentage"
          value={contract.cloudSpendPercentage ? `${contract.cloudSpendPercentage}%` : NotAvailable}
          divider={true}
        />
      </Stack>
    </Stack>
  );
};

export default ContractPageDci;
